import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Breadcrumb from "@/components/ui/breadcrumb";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import TabGroup from "@/components/ui/tab";
import Avatar from "@/components/ui/avatar";
import Chip from "@/components/ui/chip";
import ProgressBar from "@/components/ui/progress-bar";
import { LeftChevron } from "@/components/icons";
import {
  MapPin,
  Category,
  Book,
  Target,
  DocumentModal,
  Calendar,
  Eye as EyeIcon,
  User,
  Phone,
  Sent,
  EmployeeDemographics,
} from "@/components/icons";
import type { ClientCertificate } from "@/features/client-certificates/types/client-certificates.types";
import { clientCertificatesApi } from "@/features/client-certificates/services/client-certificates.api";
import CertificateCard from "@/features/client-certificates/components/CertificateCard";
import { EnrollmentCertificateViewModal } from "@/features/client-exams";
import { clientExamsApi } from "@/features/client-exams/services/client-exams.api";
import { generateAndDownloadCertificatePDFFromHtml } from "@/features/certificates";
import type {
  Staff,
  PersonnelTrainingRow,
  PersonnelExamRow,
  PersonnelCertificateRow,
  PersonnelLogRow,
} from "../types/staff-management.types";
import type { TableColumn, FilterField, SortOption } from "@/types/component";
import { staffManagementApi } from "../services/staff-management.api";
import { logsApi } from "@/features/logs/services/logs.api";
import {
  uekEducationsApi,
  formatUekDate,
  getUekExamDisplay,
  type UekEducation,
} from "@/lib/uek-educations.api";
import { educationManagementApi } from "@/features/education-management/services/education-management.api";
import type { EducationDetailApi } from "@/features/education-management/types/education-management.types";
import EducationDetailDrawer from "@/features/education-management/components/EducationDetailDrawer";
import ExamDetailDrawer from "@/features/exam-management/components/ExamDetailDrawer";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import cardStyles from "../components/PersonnelSummaryDrawer.module.scss";
import styles from "./PersonnelDetailPage.module.scss";

const PAGE_SIZE = 10;

/**
 * Verilen sınav satırlarından sayısal `score`'ların ortalamasını hesaplar.
 * Hiç geçerli skor yoksa `null` döner ki UI `—` gösterebilsin.
 * NOT: `exams` state'i paginated olduğu için sonuç yalnızca o anda yüklü
 * satırları yansıtır; backend toplu özet endpoint'i eklendiğinde bu
 * hesap oradan gelmeli.
 */
const computeSuccessAverage = (
  exams: { score?: string | number | null }[],
): number | null => {
  const scores = exams
    .map((e) => {
      if (e.score == null) return NaN;
      const n =
        typeof e.score === "number" ? e.score : Number(String(e.score).trim());
      return Number.isFinite(n) ? n : NaN;
    })
    .filter((n) => Number.isFinite(n)) as number[];
  if (scores.length === 0) return null;
  const sum = scores.reduce((a, b) => a + b, 0);
  return Math.round(sum / scores.length);
};

const getStaffDisplayData = (
  staff: Staff,
  successAverage: number | null,
) => {
  const completedCount = staff.completedEducationsCount ?? 0;
  const totalTrainings =
    staff.takenEducationsCount ??
    completedCount;
  const certificateCount = staff.certificatesCount ?? 0;
  const absenteeismPresent = staff.absencesCount ?? 0;
  const absenteeismTotal =
    staff.takenEducationsCount ??
    absenteeismPresent;

  return {
    ...staff,
    tcId: staff.identityNumber ?? "—",
    phone: staff.phoneNumber ?? "—",
    email: staff.email ?? "—",
    gender: "—",
    completedCount,
    totalTrainings,
    successAverage,
    certificateCount,
    absenteeismPresent,
    absenteeismTotal,
  };
};

const PersonnelDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { staffId } = useParams<{ staffId: string }>();
  const { hasPermission } = usePermission();
  // Audit-log erişimi: backend `/admin/audit-logs` endpoint'i AUDIT_LOGS.VIEW
  // ister; yetki yoksa tab ve "Dışa Aktar" butonu UI'dan gizlenir, istek
  // yapılmaz (aksi halde 403 loglanır).
  const canViewLogs = hasPermission(PERMISSIONS.LOGS_VIEW);
  const canViewExams = hasPermission(PERMISSIONS.EXAMS_VIEW);
  const canViewCertificates = hasPermission(PERMISSIONS.CERTIFICATES_VIEW);
  const [staff, setStaff] = useState<Staff | null>(null);
  const [loading, setLoading] = useState(true);
  type DetailTabKey = "trainings" | "uek" | "exams" | "certificates" | "logs";
  const [activeTab, setActiveTab] = useState<DetailTabKey>("trainings");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [trainingFilters, setTrainingFilters] = useState<{
    type?: number;
    status?: number;
  }>({});

  // Tab verileri (API'den; userID = staffId otomatik filtrelenir)
  const [trainings, setTrainings] = useState<PersonnelTrainingRow[]>([]);
  const [trainingsTotal, setTrainingsTotal] = useState(0);
  const [trainingsPage, setTrainingsPage] = useState(1);
  const [trainingsLoading, setTrainingsLoading] = useState(false);
  const [trainingsSort, setTrainingsSort] = useState<{
    sortBy: string;
    sortOrder: "asc" | "desc";
  }>({ sortBy: "educationName", sortOrder: "asc" });

  const [uekEducations, setUekEducations] = useState<UekEducation[]>([]);
  const [uekTotal, setUekTotal] = useState(0);
  const [uekPage, setUekPage] = useState(1);
  const [uekLoading, setUekLoading] = useState(false);

  const [exams, setExams] = useState<PersonnelExamRow[]>([]);
  const [examsTotal, setExamsTotal] = useState(0);
  const [examsPage, setExamsPage] = useState(1);
  const [examsLoading, setExamsLoading] = useState(false);
  const [examsSort, setExamsSort] = useState<{
    sortBy: string;
    sortOrder: "asc" | "desc";
  }>({ sortBy: "examName", sortOrder: "asc" });

  const [certificates, setCertificates] = useState<PersonnelCertificateRow[]>([]);
  const [, setCertificatesTotal] = useState(0);
  const [certificatesPage, setCertificatesPage] = useState(1);
  const [certificatesLoading, setCertificatesLoading] = useState(false);

  const [logs, setLogs] = useState<PersonnelLogRow[]>([]);
  const [, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsLoading, setLogsLoading] = useState(false);

  const [educationDetailDrawerOpen, setEducationDetailDrawerOpen] = useState(false);
  const [selectedEducationDetail, setSelectedEducationDetail] = useState<EducationDetailApi | null>(null);
  const [educationDetailLoading, setEducationDetailLoading] = useState(false);

  const [isExamDetailDrawerOpen, setIsExamDetailDrawerOpen] = useState(false);
  const [selectedExamIdForDetail, setSelectedExamIdForDetail] = useState<string | null>(null);
  const [viewCertificate, setViewCertificate] = useState<ClientCertificate | null>(null);

  // Search debounce (300ms) for all detail tab queries
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setTrainingsPage(1);
      setUekPage(1);
      setExamsPage(1);
      setCertificatesPage(1);
      setLogsPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchTrainings = useCallback(async () => {
    if (!staffId) return;
    setTrainingsLoading(true);
    try {
      const { data, total } = await staffManagementApi.getPersonnelTrainings(
        staffId,
        {
          page: trainingsPage,
          limit: PAGE_SIZE,
          search: debouncedSearch || undefined,
          type: trainingFilters.type,
          status: trainingFilters.status,
          sortBy: trainingsSort.sortBy,
          sortOrder: trainingsSort.sortOrder,
        }
      );
      setTrainings(data);
      setTrainingsTotal(total);
    } catch {
      setTrainings([]);
      setTrainingsTotal(0);
    } finally {
      setTrainingsLoading(false);
    }
  }, [
    staffId,
    trainingsPage,
    debouncedSearch,
    trainingFilters.type,
    trainingFilters.status,
    trainingsSort.sortBy,
    trainingsSort.sortOrder,
  ]);

  const fetchUekEducations = useCallback(async () => {
    if (!staffId) return;
    setUekLoading(true);
    try {
      const { data, total } = await uekEducationsApi.getByUser(staffId, {
        page: uekPage,
        limit: PAGE_SIZE,
        search: debouncedSearch || undefined,
      });
      setUekEducations(data);
      setUekTotal(total);
    } catch {
      setUekEducations([]);
      setUekTotal(0);
    } finally {
      setUekLoading(false);
    }
  }, [staffId, uekPage, debouncedSearch]);

  const fetchExams = useCallback(async () => {
    if (!staffId) return;
    setExamsLoading(true);
    try {
      const { data, total } = await staffManagementApi.getPersonnelExams(
        staffId,
        {
          page: examsPage,
          limit: PAGE_SIZE,
          search: debouncedSearch || undefined,
          sortBy: examsSort.sortBy,
          sortOrder: examsSort.sortOrder,
        }
      );
      setExams(data);
      setExamsTotal(total);
    } catch {
      setExams([]);
      setExamsTotal(0);
    } finally {
      setExamsLoading(false);
    }
  }, [staffId, examsPage, debouncedSearch, examsSort.sortBy, examsSort.sortOrder]);

  const fetchCertificates = useCallback(async () => {
    if (!staffId) return;
    setCertificatesLoading(true);
    try {
      const { data, total } = await staffManagementApi.getPersonnelCertificates(
        staffId,
        { page: certificatesPage, limit: PAGE_SIZE, search: debouncedSearch || undefined }
      );
      setCertificates(data);
      setCertificatesTotal(total);
    } catch {
      setCertificates([]);
      setCertificatesTotal(0);
    } finally {
      setCertificatesLoading(false);
    }
  }, [staffId, certificatesPage, debouncedSearch]);

  const fetchLogs = useCallback(async () => {
    if (!staffId) return;
    setLogsLoading(true);
    try {
      const { data, total } = await staffManagementApi.getPersonnelLogs(
        staffId,
        { page: logsPage, limit: PAGE_SIZE, search: debouncedSearch || undefined }
      );
      setLogs(data);
      setLogsTotal(total);
    } catch {
      setLogs([]);
      setLogsTotal(0);
    } finally {
      setLogsLoading(false);
    }
  }, [staffId, logsPage, debouncedSearch]);

  useEffect(() => {
    if (activeTab === "trainings") fetchTrainings();
  }, [activeTab, fetchTrainings]);

  useEffect(() => {
    if (activeTab === "uek") fetchUekEducations();
  }, [activeTab, fetchUekEducations]);

  useEffect(() => {
    if (activeTab === "exams" && canViewExams) fetchExams();
  }, [activeTab, fetchExams, canViewExams]);

  useEffect(() => {
    if (activeTab === "certificates" && canViewCertificates) fetchCertificates();
  }, [activeTab, fetchCertificates, canViewCertificates]);

  useEffect(() => {
    if (activeTab === "logs" && canViewLogs) fetchLogs();
  }, [activeTab, fetchLogs, canViewLogs]);

  const pageHeaderFilters: FilterField[] =
    activeTab === "trainings"
      ? [
          {
            name: "type",
            label: t("staffManagement.detail.cols.format"),
            type: "dropdown",
            placeholder: t("common.all"),
            options: [
              // Backend education.type enum: 0 ON_SITE, 1 ONLINE, 2 UNIT, 3 NONUNIT
              { label: t("requestAndApproval.educationTypes.faceToFace"), value: "0" },
              { label: t("requestAndApproval.educationTypes.online"), value: "1" },
              { label: t("requestAndApproval.educationTypes.unit"), value: "2" },
              { label: t("requestAndApproval.educationTypes.nonUnit"), value: "3" },
            ],
          },
          {
            name: "status",
            label: t("staffManagement.detail.cols.status"),
            type: "dropdown",
            placeholder: t("common.all"),
            options: [
              // Education status enum (query):
              // 0 - REQUEST_COLLECTION, 1 - BUDGET_PENDING, 2 - ACTIVE, 3 - CLOSED, 4 - ARCHIVED
              { label: t("educationManagement.statusOptions.talepToplama"), value: "0" },
              { label: t("educationManagement.statusOptions.butceBekliyor"), value: "1" },
              { label: t("educationManagement.statusOptions.aktif"), value: "2" },
              { label: t("educationManagement.statusOptions.kapali"), value: "3" },
              { label: t("educationManagement.statusOptions.tamamlandi"), value: "4" },
            ],
          },
        ]
      : [];

  const trainingSortOptions: SortOption[] = [
    {
      label: t("staffManagement.detail.trainingSort.nameAsc"),
      value: "educationName_asc",
    },
    {
      label: t("staffManagement.detail.trainingSort.nameDesc"),
      value: "educationName_desc",
    },
    {
      label: t("staffManagement.detail.trainingSort.typeAsc"),
      value: "type_asc",
    },
    {
      label: t("staffManagement.detail.trainingSort.typeDesc"),
      value: "type_desc",
    },
    {
      label: t("staffManagement.detail.trainingSort.statusAsc"),
      value: "status_asc",
    },
    {
      label: t("staffManagement.detail.trainingSort.statusDesc"),
      value: "status_desc",
    },
  ];

  const examSortOptions: SortOption[] = [
    {
      label: t("staffManagement.detail.examSort.nameAsc"),
      value: "examName_asc",
    },
    {
      label: t("staffManagement.detail.examSort.nameDesc"),
      value: "examName_desc",
    },
    {
      label: t("staffManagement.detail.examSort.scoreDesc"),
      value: "score_desc",
    },
    {
      label: t("staffManagement.detail.examSort.scoreAsc"),
      value: "score_asc",
    }
  ];

  // UEK kayıtları sunucuda tamamlama tarihine göre sıralı gelir; sıralama seçeneği yok.
  const pageHeaderSortOptions: SortOption[] =
    activeTab === "exams"
      ? examSortOptions
      : activeTab === "uek"
        ? []
        : trainingSortOptions;

  const handlePageHeaderSortChange = (option: SortOption) => {
    const value = option?.value ?? "";
    if (activeTab === "trainings") {
      const parts = value.includes("_")
        ? value.split("_")
        : ["educationName", "asc"];
      const sortBy = parts[0] ?? "educationName";
      const order = parts[1];
      const sortOrder: "asc" | "desc" =
        order === "asc" || order === "desc" ? order : "asc";
      setTrainingsSort({ sortBy, sortOrder });
      setTrainingsPage(1);
    } else if (activeTab === "exams") {
      const parts = value.includes("_") ? value.split("_") : ["examName", "asc"];
      const sortBy = parts[0] ?? "examName";
      const order = parts[1];
      const sortOrder: "asc" | "desc" =
        order === "asc" || order === "desc" ? order : "asc";
      setExamsSort({ sortBy, sortOrder });
      setExamsPage(1);
    }
  };

  const handlePageHeaderFilterChange = (values: Record<string, unknown>) => {
    setTrainingFilters({
      type:
        values.type != null && values.type !== ""
          ? Number(values.type)
          : undefined,
      status:
        values.status != null && values.status !== ""
          ? Number(values.status)
          : undefined,
    });
    setTrainingsPage(1);
  };

  const handleExportLogs = async () => {
    if (!staffId) return;
    try {
      const { blob, filename } = await logsApi.export({
        format: "excel",
        userID: staffId,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Export hatası sessizce bırakılabilir veya toast gösterilebilir
    }
  };

  useEffect(() => {
    if (!staffId) return;
    let cancelled = false;
    setLoading(true);
    staffManagementApi
      .getById(staffId)
      .then((data) => {
        if (!cancelled) setStaff(data);
      })
      .catch(() => {
        if (!cancelled) setStaff(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [staffId]);

  if (loading || !staff) {
    return (
      <DashboardLayout>
        <Header title={t("staffManagement.title")} />
        <div className={styles.loading}>
          {t("common.loading")}
        </div>
      </DashboardLayout>
    );
  }

  const successAverage = computeSuccessAverage(exams);
  const data = getStaffDisplayData(staff, successAverage);

  const breadcrumbItems = [
    {
      label: t("staffManagement.title"),
      href: "/dashboard/staff-management",
    },
    { label: staff.name },
  ];

  const handleOpenEducationDetail = (row: PersonnelTrainingRow) => {
    if (!row.educationId) return;
    setSelectedEducationDetail(null);
    setEducationDetailLoading(true);
    setEducationDetailDrawerOpen(true);
    educationManagementApi
      .getById(row.educationId)
      .then((res) => setSelectedEducationDetail(res.data))
      .catch(() => setSelectedEducationDetail(null))
      .finally(() => setEducationDetailLoading(false));
  };

  const handleOpenExamDetail = (row: PersonnelExamRow) => {
    setSelectedExamIdForDetail(row.examId || null);
    setIsExamDetailDrawerOpen(true);
  };

  const handleCloseExamDetailDrawer = () => {
    setIsExamDetailDrawerOpen(false);
    setSelectedExamIdForDetail(null);
  };

  const handleCertificateAction = async (
    certificate: ClientCertificate,
    action: string,
  ) => {
    if (action === "view") {
      setViewCertificate(certificate);
    } else if (action === "download") {
      const filename =
        (certificate.title || "sertifika")
          .replace(/[^\w\s-]/gi, "")
          .replace(/\s+/g, "-") || "sertifika";

      try {
        // Önce response ile gelen HTML varsa direkt client-side PDF üret
        if (certificate.rawHtml) {
          await generateAndDownloadCertificatePDFFromHtml(
            certificate.rawHtml,
            filename,
          );
          return;
        }

        // HTML yoksa, gerekirse backend'den alıp yine client-side üret
        if (!certificate.enrollmentId) return;

        let html: string | null = null;

        if (certificate.educationId && certificate.enrollmentId) {
          const data = await clientExamsApi.getEnrollmentCertificate(
            certificate.educationId,
            certificate.enrollmentId,
          );
          // getEnrollmentCertificate dönüş tipi parsedHtml alanını içerir
          html = (data as { parsedHtml?: string | null }).parsedHtml ?? null;
        }

        if (!html) {
          html = await clientCertificatesApi.getCertificateHtml(
            certificate.enrollmentId,
          );
        }

        if (html) {
          await generateAndDownloadCertificatePDFFromHtml(html, filename);
        } else {
          clientCertificatesApi.downloadCertificatePdf(certificate.enrollmentId);
        }
      } catch {
        if (certificate.enrollmentId) {
          clientCertificatesApi.downloadCertificatePdf(certificate.enrollmentId);
        }
      }
    }
  };

  const detailTabs: { key: DetailTabKey; label: string; content: null }[] = [
    {
      key: "trainings",
      label: t("staffManagement.detail.tabs.trainings"),
      content: null,
    },
    // Sağ kolonda 5 sekme eşit bölündüğü için uzun ad sığmıyor; kısa etiket kullanılır.
    { key: "uek", label: t("uekEducations.tabShort"), content: null },
    ...(canViewExams
      ? [
          {
            key: "exams" as const,
            label: t("staffManagement.detail.tabs.exams"),
            content: null,
          },
        ]
      : []),
    ...(canViewCertificates
      ? [
          {
            key: "certificates" as const,
            label: t("staffManagement.detail.tabs.certificates"),
            content: null,
          },
        ]
      : []),
    ...(canViewLogs
      ? [
          {
            key: "logs" as const,
            label: t("staffManagement.detail.tabs.logs"),
            content: null,
          },
        ]
      : []),
  ];

  const activeTabIndex = Math.max(
    0,
    detailTabs.findIndex((tab) => tab.key === activeTab),
  );

  // Table columns per tab
  const trainingColumns: TableColumn<PersonnelTrainingRow>[] = [
    {
      header: t("staffManagement.detail.cols.trainingName"),
      accessor: (r) => r.trainingName,
    },
    {
      header: t("staffManagement.detail.cols.format"),
      accessor: (r) => r.format,
    },
    {
      header: t("staffManagement.detail.cols.progress"),
      accessor: (r) => (
        <div className={styles.progressCell}>
          <ProgressBar value={r.progress} showLabel={false} size="small" />
          <span>%{r.progress}</span>
        </div>
      ),
    },
    {
      header: t("staffManagement.detail.cols.status"),
      accessor: (r) => (
        <Chip type={r.statusType} size="small">
          {r.status}
        </Chip>
      ),
    },
    {
      header: t("staffManagement.detail.cols.action"),
      accessor: (row) => (
        <button
          type="button"
          className={styles.iconButton}
          aria-label={t("common.view")}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenEducationDetail(row);
          }}
        >
          <EyeIcon width={20} height={20} />
        </button>
      ),
    },
  ];

  const uekColumns: TableColumn<UekEducation>[] = [
    {
      header: t("uekEducations.cols.educationName"),
      accessor: (r) => r.educationName,
    },
    {
      header: t("uekEducations.cols.format"),
      accessor: (r) => r.educationFormat ?? "—",
    },
    {
      header: t("uekEducations.cols.type"),
      accessor: (r) => r.educationType ?? "—",
    },
    {
      header: t("uekEducations.cols.completedAt"),
      accessor: (r) => formatUekDate(r.completedAt),
    },
    {
      header: t("uekEducations.cols.exam"),
      accessor: (r) => {
        const exam = getUekExamDisplay(r);
        return (
          <Chip type={exam.type} size="small">
            {exam.labelKey ? t(exam.labelKey) : exam.label}
          </Chip>
        );
      },
    },
  ];

  const examColumns: TableColumn<PersonnelExamRow>[] = [
    {
      header: t("staffManagement.detail.cols.examName"),
      accessor: (r) => r.examName,
    },
    {
      header: t("staffManagement.detail.cols.score"),
      accessor: (r) => r.score,
    },
    {
      header: t("staffManagement.detail.cols.status"),
      accessor: (r) => (
        <Chip type={r.statusType} size="small">
          {r.status}
        </Chip>
      ),
    },
    {
      header: t("staffManagement.detail.cols.action"),
      accessor: (row) => (
        <button
          type="button"
          className={styles.iconButton}
          aria-label={t("common.view")}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenExamDetail(row);
          }}
        >
          <EyeIcon width={20} height={20} />
        </button>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <Header title={t("staffManagement.title")} />
      <div className={styles.breadcrumbContainer}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate("/dashboard/staff-management")}
          aria-label={t("common.back")}
        >
          <LeftChevron width={20} height={20} />
        </button>
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className={styles.pageLayout}>
        <aside className={styles.leftColumn}>
          <div className={cardStyles.personnelCard}>
            <div className={cardStyles.personnelCardTop}>
              <div className={cardStyles.avatarWrap}>
                <Avatar alt={staff.name} color="primary" />
              </div>
              <div className={cardStyles.personnelInfo}>
                <div className={cardStyles.personnelHeader}>
                  <div className={cardStyles.personnelNameTitle}>
                    <h2 className={cardStyles.personnelName}>{staff.name}</h2>
                    <p className={cardStyles.personnelTitle}>{staff.title}</p>
                  </div>
                  <div className={cardStyles.badgesRow}>
                    <Chip type="success" size="small">
                      {t("staffManagement.status.active")}
                    </Chip>
                  </div>
                </div>
              </div>
            </div>
            <div className={cardStyles.contactPills}>
              <span className={cardStyles.contactPill}>TC {data.tcId}</span>
              <span className={cardStyles.contactPill}>{data.phone}</span>
              <span className={cardStyles.contactPill}>{data.email}</span>
            </div>
            <section className={cardStyles.section}>
              <h4 className={cardStyles.sectionTitle}>
                {t("staffManagement.personnelSummary.personalInfo")}
              </h4>
              <div className={cardStyles.sectionCard}>
                <div className={cardStyles.detailList}>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon}>
                      <User width={20} height={20} />
                    </span>
                    <span className={cardStyles.detailLabel}>TC</span>
                    <span className={cardStyles.detailValue}>{data.tcId}</span>
                  </div>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon}>
                      <Phone width={20} height={20} />
                    </span>
                    <span className={cardStyles.detailLabel}>
                      {t("staffManagement.detail.phone")}
                    </span>
                    <span className={cardStyles.detailValue}>{data.phone}</span>
                  </div>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon}>
                      <Sent width={20} height={20} />
                    </span>
                    <span className={cardStyles.detailLabel}>
                      {t("staffManagement.detail.email")}
                    </span>
                    <span className={cardStyles.detailValue}>
                      <a
                        href={`mailto:${data.email}`}
                        className={styles.emailLink}
                      >
                        {data.email}
                      </a>
                    </span>
                  </div>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon}>
                      <EmployeeDemographics width={20} height={20} />
                    </span>
                    <span className={cardStyles.detailLabel}>
                      {t("staffManagement.detail.gender")}
                    </span>
                    <span className={cardStyles.detailValue}>
                      {data.gender}
                    </span>
                  </div>
                </div>
              </div>
            </section>
            <section className={cardStyles.section}>
              <h4 className={cardStyles.sectionTitle}>
                {t("staffManagement.personnelSummary.corporateInfo")}
              </h4>
              <div className={cardStyles.sectionCard}>
                <div className={cardStyles.detailList}>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon}>
                      <MapPin width={20} height={20} />
                    </span>
                    <span className={cardStyles.detailLabel}>
                      {t("staffManagement.personnelSummary.location")}
                    </span>
                    <span className={cardStyles.detailValue}>
                      {staff.location}
                    </span>
                  </div>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon}>
                      <Category width={20} height={20} />
                    </span>
                    <span className={cardStyles.detailLabel}>
                      {t("staffManagement.personnelSummary.department")}
                    </span>
                    <span className={cardStyles.detailValue}>
                      {staff.department}
                    </span>
                  </div>
                </div>
              </div>
            </section>
            <section className={cardStyles.section}>
              <h4 className={cardStyles.sectionTitle}>
                {t("staffManagement.personnelSummary.educationInfo")}
              </h4>
              <div className={cardStyles.sectionCard}>
                <div className={cardStyles.detailList}>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon} aria-hidden>
                      <Book width={20} height={20} color="#707a8f" />
                    </span>
                    <span className={cardStyles.detailLabel}>
                      {t("staffManagement.personnelSummary.completedTrainings")}
                    </span>
                    <span className={cardStyles.detailValue}>
                      {data.completedCount} / {data.totalTrainings}
                    </span>
                  </div>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon} aria-hidden>
                      <Target width={20} height={20} color="#707a8f" />
                    </span>
                    <span className={cardStyles.detailLabel}>
                      {t("staffManagement.personnelSummary.successAverage")}
                    </span>
                    <span className={cardStyles.detailValue}>
                      {data.successAverage != null
                        ? `%${data.successAverage}`
                        : "—"}
                    </span>
                  </div>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon} aria-hidden>
                      <DocumentModal width={20} height={20} color="#707a8f" />
                    </span>
                    <span className={cardStyles.detailLabel}>
                      {t("staffManagement.personnelSummary.certificateCount")}
                    </span>
                    <span className={cardStyles.detailValue}>
                      {data.certificateCount}
                    </span>
                  </div>
                  <div className={cardStyles.detailItem}>
                    <span className={cardStyles.detailIcon} aria-hidden>
                      <Calendar width={20} height={20} color="#707a8f" />
                    </span>
                    <span className={cardStyles.detailLabel}>
                      {t("staffManagement.personnelSummary.absenteeism")}
                    </span>
                    <span className={cardStyles.detailValue}>
                      {data.absenteeismPresent} / {data.absenteeismTotal}
                    </span>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </aside>

        <div className={styles.rightColumn}>
          <TabGroup
            tabs={detailTabs}
            activeTab={activeTabIndex}
            onTabChange={(index: number) => {
              const next = detailTabs[index]?.key;
              if (next) setActiveTab(next);
            }}
            fullWidth
          />

          <PageHeader
            title=""
            searchPlaceholder={t("common.search")}
            searchValue={searchTerm}
            onSearch={setSearchTerm}
            showSort
            sortOptions={pageHeaderSortOptions}
            onSort={handlePageHeaderSortChange}
            filters={pageHeaderFilters}
            onFilterChange={handlePageHeaderFilterChange}
            onAddNew={
              activeTab === "logs" && canViewLogs ? handleExportLogs : undefined
            }
            addButtonLabel={
              activeTab === "logs" && canViewLogs
                ? t("common.export")
                : undefined
            }
            className={styles.pageHeader}
          />

          <div className={styles.tableSection}>
            {activeTab === "trainings" && (
              <Table<PersonnelTrainingRow>
                columns={trainingColumns}
                data={trainings}
                getRowId={(r) => r.id}
                isLoading={trainingsLoading}
                renderActions={() => null}
                hasVisibleActions={false}
                totalRecords={trainingsTotal}
                pageSize={PAGE_SIZE}
                currentPage={trainingsPage}
                onPageChange={setTrainingsPage}
                contentBasedHeight
                emptyMessage={t("staffManagement.detail.noData")}
              />
            )}
            {activeTab === "uek" && (
              <Table<UekEducation>
                columns={uekColumns}
                data={uekEducations}
                getRowId={(r) => r.id}
                isLoading={uekLoading}
                renderActions={() => null}
                hasVisibleActions={false}
                totalRecords={uekTotal}
                pageSize={PAGE_SIZE}
                currentPage={uekPage}
                onPageChange={setUekPage}
                contentBasedHeight
                emptyMessage={t("uekEducations.noData")}
              />
            )}
            {activeTab === "exams" && canViewExams && (
              <Table<PersonnelExamRow>
                columns={examColumns}
                data={exams}
                getRowId={(r) => r.id}
                isLoading={examsLoading}
                renderActions={() => null}
                hasVisibleActions={false}
                totalRecords={examsTotal}
                pageSize={PAGE_SIZE}
                currentPage={examsPage}
                onPageChange={setExamsPage}
                contentBasedHeight
                emptyMessage={t("staffManagement.detail.noData")}
              />
            )}
            {activeTab === "certificates" && canViewCertificates && (
              <div className={styles.certificateList}>
                {certificatesLoading ? (
                  <p className={styles.emptyText}>
                    {t("common.loading")}
                  </p>
                ) : certificates.length === 0 ? (
                  <p className={styles.emptyText}>
                    {t("staffManagement.detail.noData")}
                  </p>
                ) : (
                  <div className={styles.certificateGrid}>
                    {certificates.map((cert) => {
                      const clientCert: ClientCertificate = {
                        id: cert.id,
                        title: cert.name,
                        relatedTraining: cert.relatedEducation,
                        issueDate: cert.createdAt,
                        certificateNo: cert.certificateNo ?? "",
                        imageUrl: cert.imageUrl,
                        rawHtml: cert.parsedHtml,
                        enrollmentId: cert.enrollmentId,
                        educationId: cert.educationId,
                      };

                      return (
                        <CertificateCard
                          key={clientCert.id}
                          certificate={clientCert}
                          onActionClick={handleCertificateAction}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {activeTab === "logs" && canViewLogs && (
              <div className={styles.logTimeline}>
                {logsLoading ? (
                  <p className={styles.emptyText}>
                    {t("common.loading")}
                  </p>
                ) : logs.length === 0 ? (
                  <p className={styles.emptyText}>
                    {t("staffManagement.detail.noData")}
                  </p>
                ) : (
                  <>
                    <div className={styles.logTimelineLine} aria-hidden />
                    {logs.map((log) => (
                      <div key={log.id} className={styles.logEntry}>
                        <span className={styles.logTime}>{log.dateTime}</span>
                        <div className={styles.logDotWrap}>
                          <div className={styles.logDot} />
                        </div>
                        <div className={styles.logCard}>
                          <div className={styles.logCardContent}>
                            <h4 className={styles.logCardTitle}>{log.title}</h4>
                            <p className={styles.logCardDescription}>
                              {log.description}
                            </p>
                          </div>
                          <Chip type="info" size="small">
                            {log.badge}
                          </Chip>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <EnrollmentCertificateViewModal
        educationId={viewCertificate?.educationId}
        enrollmentId={viewCertificate?.enrollmentId}
        titleOverride={viewCertificate?.title}
        isOpen={Boolean(viewCertificate)}
        onClose={() => setViewCertificate(null)}
        onDownload={(enrollmentId) => {
          clientCertificatesApi.downloadCertificatePdf(enrollmentId);
        }}
      />

      <EducationDetailDrawer
        isOpen={educationDetailDrawerOpen}
        onClose={() => {
          setEducationDetailDrawerOpen(false);
          setSelectedEducationDetail(null);
        }}
        educationDetail={selectedEducationDetail}
        isLoading={educationDetailLoading}
      />

      <ExamDetailDrawer
        isOpen={isExamDetailDrawerOpen}
        onClose={handleCloseExamDetailDrawer}
        examId={selectedExamIdForDetail}
      />
    </DashboardLayout>
  );
};

export default PersonnelDetailPage;
